import _ from 'lodash'
import axios from 'axios'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Autocomplete from 'react-autocomplete'
import FormFieldLayout from 'layouts/FormFieldLayout'
import AutocompleteResult from 'components/AutocompleteResult'
import AutocompleteList from 'components/AutocompleteList'
import { sendEvent } from 'hoc/WithInputAnalytics/utils'
import { parseAddress } from './actions'
import {
  ADDRESS_ID_KEY,
  ADDRESS_TEXT_KEY,
  AUTOCOMPLETE_MIN_CHARS,
  ITEM_MONIKER_TOO_MANY,
  ITEM_MONIKER_TOO_SHORT,
  SWITCH_MANUAL_ADDRESS,
  TOO_MANY_ERROR_STATUS,
  HAS_RESULTS_STATUS,
  NO_MATCHES_STATUS,
} from './constants'

class AutocompleteAddress extends Component {
  static propTypes = {
    onManualEntry: PropTypes.func,
    onSelect: PropTypes.func,
    searchForAddress: PropTypes.func,
    gaCategory: PropTypes.string,
    value: PropTypes.string,
    loading: PropTypes.bool,
    errorMessage: PropTypes.string,
    label: PropTypes.string,
  }

  static defaultProps = {
    onManualEntry: f => f,
    gaCategory: 'Address Autocomplete',
    value: '',
    loading: false,
    onSelect: f => f,
    errorMessage: '',
    label: 'Address',
    searchForAddress: f => f,
  }

  constructor(props) {
    super(props)
    this.focusRef = React.createRef()
    this.state = {
      isSearching: false,
      searchResults: [],
      searchError: undefined,
      searchState: null,
      value: props.value,
    }
    this.searchCanceller = axios.CancelToken.source()
  }

  componentDidMount() {
    this.handleSearchDebounced = _
      .debounce((addressValue) => {
        this.setState({
          isSearching: true,
          value: addressValue,
        })
        this.searchCanceller = axios.CancelToken.source()
        this.props.searchForAddress(addressValue, this.searchCanceller.token)
          .then((data) => {
            this.setState({
              searchResults: data.results,
              searchState: data.state,
              searchError: '',
            }, () => {
              this.trackSearchResults()
            })
            // If there is no other requests in-flight, not searching any more
            if (this.state.value === addressValue) {
              this.setState({
                isSearching: false,
              })
            }
          })
          .catch((error) => {
            this.setState({
              searchResults: [],
              searchState: null,
              searchError: error,
            })
            // If there is no other requests in-flight, not searching any more
            if (this.state.value === addressValue) {
              this.setState({
                isSearching: false,
              })
            }
          })
      }, 400)
    this.handleSearchDebounced.bind(this)
  }

  componentWillUnmount() {
    this.handleSearchDebounced.cancel()
    this.searchCanceller.cancel()
  }

  onInputUpdated = (event) => {
    const val = event.target.value
    if (val && val.length > AUTOCOMPLETE_MIN_CHARS) {
      this.handleSearchDebounced(val)
    }
    this.setState({ value: val })
  }

  onItemSelected = (text, item) => {
    switch (item.Moniker) {
      case SWITCH_MANUAL_ADDRESS:
        this.trackEvent('Enter Manual Address', 'Cant find', text.length)
        this.props.onManualEntry(true)
        break

      case ITEM_MONIKER_TOO_MANY:
        this.trackEvent('Enter Manual Address', 'Too many matches', text.length)
        this.props.onManualEntry(true)
        break

      default:
        this.trackEvent('Select address', undefined, text.length)
        this.setState({ value: text })
        this.props.onSelect({
          [ADDRESS_ID_KEY]: item.Moniker,
          [ADDRESS_TEXT_KEY]: text,
        })
    }
  }

  /* Track when a particular error is displayed */
  trackSearchResults = () => {
    const { searchState, value } = this.state

    if (searchState === TOO_MANY_ERROR_STATUS) {
      this.trackEvent('Search Completed', 'Too many matches', value.length)
    } else if (searchState === HAS_RESULTS_STATUS) {
      this.trackEvent('Search Completed', 'Has Results', value.length)
    } else if (searchState === NO_MATCHES_STATUS) {
      this.trackEvent('Search Completed', 'No Results')
    }
  }

  trackEvent = (action, label, value) => sendEvent(label, action, this.props.gaCategory, value)
  focus = () => this.focusRef.current.focus()
  handleFocus = e => this.trackEvent('Focussed', e.currentTarget.name)
  render() {
    const { loading, errorMessage, label } = this.props

    const {
      isSearching, searchResults, searchError, searchState, value,
    } = this.state

    const hasValueClass = value.length === 0 || 'has-value'
    const hasError = errorMessage.length > 0 || false
    const errorObject = { status: hasError, message: errorMessage }

    const results = [...searchResults]
    if (value.length <= AUTOCOMPLETE_MIN_CHARS) {
      results.push({
        Moniker: ITEM_MONIKER_TOO_SHORT,
        Picklist: '',
      })
    } else if (searchState === TOO_MANY_ERROR_STATUS) {
      results.push({
        Moniker: ITEM_MONIKER_TOO_MANY,
        Picklist: '',
      })
    } else {
      results.push({
        Moniker: SWITCH_MANUAL_ADDRESS,
        Picklist: '',
      })
    }

    results.forEach((ignored, i) => {
      results[i].Picklist = parseAddress(results[i].Picklist)
    })

    const fieldElement = props => (
      <FormFieldLayout
        fieldName={ADDRESS_TEXT_KEY}
        labelText={label}
        fieldElement={
          <input
            {...props}
            type="text"
            name="street-address"
            className={`form-input ${hasValueClass}`}
            id={ADDRESS_TEXT_KEY}
            maxLength="600"
            disabled={loading}
            required
            autoComplete="googleignoreautofill" // Google deliberately ignores off and false values
            ref={(r) => {
              props.ref(r)
              this.focusRef.current = r
            }}
            onFocus={this.handleFocus}
            placeholder={label}
          />
        }
        error={errorObject}
      />
    )

    return (
      <Autocomplete
        getItemValue={item => item.Picklist}
        items={results}
        renderItem={(item, isHighlighted) => (
          <AutocompleteResult
            key={item.Moniker}
            item={item}
            search={value}
            isHighlighted={isHighlighted}
          />
        )}
        renderMenu={items => (
          <AutocompleteList
            searching={isSearching}
            searchError={searchError}
            searchState={searchState}
          >
            {items}
          </AutocompleteList>
        )}
        renderInput={fieldElement}
        value={value}
        onChange={this.onInputUpdated}
        onSelect={this.onItemSelected}
        selectOnBlur={false}
        wrapperStyle={{ width: '100%' }}
      />
    )
  }
}

export default AutocompleteAddress



// WEBPACK FOOTER //
// ./components/AutocompleteAddress/index.jsx